"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OcsValidationCheckType = exports.AmrValidationCheckType = exports.AbsoluteMaximumRatingsDenormalizedSchema = exports.OperatingCharacteristicsDenormalizedSchema = exports.BoundaryConditionDenormalizedSchema = void 0;
exports.validateAndCleanDenormalizedAMRs = validateAndCleanDenormalizedAMRs;
exports.validateAndCleanDenormalizedOCs = validateAndCleanDenormalizedOCs;
exports.mapBoundarySignalNamesToSignalIds = mapBoundarySignalNamesToSignalIds;
exports.convertHilAmrToDenormalized = convertHilAmrToDenormalized;
exports.convertHilOcToDenormalized = convertHilOcToDenormalized;
exports.convertBoundariesToAmrDenormalized = convertBoundariesToAmrDenormalized;
exports.convertBoundariesToOcDenormalized = convertBoundariesToOcDenormalized;
exports.remapSignalIdsInBoundaries = remapSignalIdsInBoundaries;
const z = require("zod");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
exports.BoundaryConditionDenormalizedSchema = sigma_engine_shared_1.HilOperatingConditionBoundarySchema.omit({
    signalId: true,
    minimumRelativeToSignalId: true,
    nominalRelativeToSignalId: true,
    maximumRelativeToSignalId: true,
})
    .extend({
    signalName: z.string().describe('The signal name that the operating characteristic applies to.'),
    minimum: z
        .string()
        .nullable()
        .describe('The minimum value of the characteristic in the appropriate SI units for the characteristic.'),
    minimumRelativeToSignalName: z
        .string()
        .nullable()
        .describe('The name of the signal that the minimum value is relative to, if the characteristic requires a relative signal. Characteristics like Voltage and Power require a relative signal, while others like Current and Temperature do not.'),
    nominal: z
        .string()
        .nullable()
        .describe('The nominal value of the characteristic in the appropriate SI units for the characteristic.'),
    nominalRelativeToSignalName: z
        .string()
        .nullable()
        .describe('The name of the signal that the nominal value is relative to, if the characteristic requires a relative signal. Characteristics like Voltage and Power require a relative signal, while others like Current and Temperature do not.'),
    maximum: z
        .string()
        .nullable()
        .describe('The maximum value of the characteristic in the appropriate SI units for the characteristic.'),
    maximumRelativeToSignalName: z
        .string()
        .nullable()
        .describe('The name of the signal that the maximum value is relative to, if the characteristic requires a relative signal. Characteristics like Voltage and Power require a relative signal, while others like Current and Temperature do not.'),
})
    .strict()
    .describe(sigma_engine_shared_1.OPERATING_CONDITION_BOUNDARY_DESCRIPTION);
exports.OperatingCharacteristicsDenormalizedSchema = exports.BoundaryConditionDenormalizedSchema;
exports.AbsoluteMaximumRatingsDenormalizedSchema = exports.BoundaryConditionDenormalizedSchema.omit({
    nominal: true,
    nominalRelativeToSignalName: true,
}).describe(sigma_engine_shared_1.OPERATING_CONDITION_BOUNDARY_DESCRIPTION);
var AmrValidationCheckType;
(function (AmrValidationCheckType) {
    AmrValidationCheckType["ValuesShouldBeNumericSIParseable"] = "ValuesShouldBeNumericSIParseable";
    AmrValidationCheckType["RelativeSignalShouldBeValidSignal"] = "RelativeSignalShouldBeValidSignal";
    AmrValidationCheckType["RelativeSignalShouldNotBeMissing"] = "RelativeSignalShouldNotBeMissing";
    AmrValidationCheckType["RatingShouldHaveAtLeastOneTargetSignal"] = "RatingShouldHaveAtLeastOneTargetSignal";
    AmrValidationCheckType["TargetAndRelativeSignalShouldNotBeEqual"] = "TargetAndRelativeSignalShouldNotBeEqual";
    AmrValidationCheckType["RatingsShouldNotBeDuplicated"] = "RatingsShouldNotBeDuplicated";
    AmrValidationCheckType["MinimumAndMaximumShouldDiffer"] = "MinimumAndMaximumShouldDiffer";
    AmrValidationCheckType["AllNonGroundNonNoConnectSignalsShouldHaveAmr"] = "AllNonGroundNonNoConnectSignalsShouldHaveAmr";
    AmrValidationCheckType["ParentRowLineageShouldMatchRawRow"] = "ParentRowLineageShouldMatchRawRow";
})(AmrValidationCheckType || (exports.AmrValidationCheckType = AmrValidationCheckType = {}));
var OcsValidationCheckType;
(function (OcsValidationCheckType) {
    OcsValidationCheckType["ValuesShouldBeNumericSIParseable"] = "ValuesShouldBeNumericSIParseable";
    OcsValidationCheckType["RelativeSignalShouldBeValidSignal"] = "RelativeSignalShouldBeValidSignal";
    OcsValidationCheckType["RelativeSignalShouldNotBeMissing"] = "RelativeSignalShouldNotBeMissing";
    OcsValidationCheckType["ConditionShouldHaveAtLeastOneTargetSignal"] = "ConditionShouldHaveAtLeastOneTargetSignal";
    OcsValidationCheckType["GroundSignalsShouldNotBeAssignedConditions"] = "GroundSignalsShouldNotBeAssignedConditions";
    OcsValidationCheckType["TargetAndRelativeSignalShouldNotBeEqual"] = "TargetAndRelativeSignalShouldNotBeEqual";
    OcsValidationCheckType["ConditionsShouldNotBeDuplicated"] = "ConditionsShouldNotBeDuplicated";
    OcsValidationCheckType["MinimumNominalAndMaximumShouldDiffer"] = "MinimumNominalAndMaximumShouldDiffer";
    OcsValidationCheckType["ParentRowLineageShouldMatchRawRow"] = "ParentRowLineageShouldMatchRawRow";
})(OcsValidationCheckType || (exports.OcsValidationCheckType = OcsValidationCheckType = {}));
function validateAndCleanDenormalizedAMRs(amrs, signals, logger, characteristic = sigma_engine_shared_1.BoundaryCharacteristic.Voltage) {
    let validatedResults = amrs;
    const allValidationResults = [];
    const { validatedEntries: afterMinSICheck, validationResults: minSIResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueSIParseability(entries, 'minimum', characteristic, {
        ignoreEmpty: true,
    }), AmrValidationCheckType.ValuesShouldBeNumericSIParseable, { dropFailingEntries: true, logger });
    validatedResults = afterMinSICheck;
    allValidationResults.push(...minSIResults);
    const { validatedEntries: afterMaxSICheck, validationResults: maxSIResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueSIParseability(entries, 'maximum', characteristic, {
        ignoreEmpty: true,
    }), AmrValidationCheckType.ValuesShouldBeNumericSIParseable, { dropFailingEntries: true, logger });
    validatedResults = afterMaxSICheck;
    allValidationResults.push(...maxSIResults);
    const signalNames = signals.map((s) => s.signalName);
    const signalNamesSet = new Set(signalNames);
    const { validatedEntries: afterMinRelativeCheck, validationResults: minRelativeResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueBelongsToSet(entries, 'minimumRelativeToSignalName', signalNamesSet, {
        ignoreEmpty: true,
    }), AmrValidationCheckType.RelativeSignalShouldBeValidSignal, { dropFailingEntries: true, logger });
    validatedResults = afterMinRelativeCheck;
    allValidationResults.push(...minRelativeResults);
    const { validatedEntries: afterMaxRelativeCheck, validationResults: maxRelativeResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueBelongsToSet(entries, 'maximumRelativeToSignalName', signalNamesSet, {
        ignoreEmpty: true,
    }), AmrValidationCheckType.RelativeSignalShouldBeValidSignal, { dropFailingEntries: true, logger });
    validatedResults = afterMaxRelativeCheck;
    allValidationResults.push(...maxRelativeResults);
    const { validatedEntries: afterMinTargetCheck, validationResults: minTargetResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkPropertyValuesDiffer(entries, 'signalName', 'minimumRelativeToSignalName'), AmrValidationCheckType.TargetAndRelativeSignalShouldNotBeEqual, { dropFailingEntries: true, logger });
    validatedResults = afterMinTargetCheck;
    allValidationResults.push(...minTargetResults);
    const { validatedEntries: afterMaxTargetCheck, validationResults: maxTargetResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkPropertyValuesDiffer(entries, 'signalName', 'maximumRelativeToSignalName'), AmrValidationCheckType.TargetAndRelativeSignalShouldNotBeEqual, { dropFailingEntries: true, logger });
    validatedResults = afterMaxTargetCheck;
    allValidationResults.push(...maxTargetResults);
    const { validatedEntries: afterMinMaxDifferCheck, validationResults: minMaxDifferResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => checkMinimumAndMaximumDiffer(entries, characteristic), AmrValidationCheckType.MinimumAndMaximumShouldDiffer, { dropFailingEntries: true, logger });
    validatedResults = afterMinMaxDifferCheck;
    allValidationResults.push(...minMaxDifferResults);
    const { validatedEntries: afterDuplicateCheck, validationResults: duplicateResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkForDuplicateEntries(entries), AmrValidationCheckType.RatingsShouldNotBeDuplicated, { dropFailingEntries: true, logger });
    validatedResults = afterDuplicateCheck;
    allValidationResults.push(...duplicateResults);
    const missingSignalsResult = checkAllSignalsHaveAmr(validatedResults, signals);
    allValidationResults.push(missingSignalsResult);
    return {
        validatedResults,
        validationResults: (0, sigma_engine_shared_1.compactValidationResults)(allValidationResults),
    };
}
function checkMinimumAndMaximumDiffer(entries, characteristic = sigma_engine_shared_1.BoundaryCharacteristic.Voltage) {
    const failures = [];
    const siDefinition = sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[characteristic];
    entries.forEach((entry, i) => {
        if (entry.minimum === null || entry.maximum === null) {
            return;
        }
        try {
            const minValue = sigma_engine_shared_1.Utils.parseNumericalSIValue(entry.minimum, siDefinition);
            const maxValue = sigma_engine_shared_1.Utils.parseNumericalSIValue(entry.maximum, siDefinition);
            if (minValue !== undefined && maxValue !== undefined && Math.abs(minValue - maxValue) < siDefinition.epsilon) {
                failures.push({
                    index: i,
                    message: `Minimum and maximum values are equal: ${entry.minimum} === ${entry.maximum}`,
                    details: `Signal: ${entry.signalName}, Characteristic: ${entry.characteristic}`,
                });
            }
        }
        catch (error) {
            return;
        }
    });
    return failures;
}
function checkAllSignalsHaveAmr(amrs, signals) {
    const amrSignalNames = new Set(amrs.map((a) => a.signalName));
    const requiredSignals = signals.filter((s) => !s.ground && !s.noConnect);
    const missingSignals = requiredSignals.filter((s) => !amrSignalNames.has(s.signalName));
    if (missingSignals.length === 0) {
        return {
            type: AmrValidationCheckType.AllNonGroundNonNoConnectSignalsShouldHaveAmr,
            message: 'All required signals have AMR entries',
            result: sigma_engine_shared_1.ValidationCheckResultStatus.pass,
        };
    }
    const missingNames = missingSignals.map((s) => s.signalName).join(', ');
    return {
        type: AmrValidationCheckType.AllNonGroundNonNoConnectSignalsShouldHaveAmr,
        message: `Missing AMR entries for ${missingSignals.length} signals: ${missingNames}`,
        result: sigma_engine_shared_1.ValidationCheckResultStatus.fail,
        failures: missingSignals.map((s, i) => ({
            index: i,
            message: `Signal '${s.signalName}' is missing an AMR entry`,
            details: `Category: power=${s.power}, analog=${s.analog}, digital=${s.digital}`,
        })),
    };
}
function validateAndCleanDenormalizedOCs(ocs, signals, logger, characteristic = sigma_engine_shared_1.BoundaryCharacteristic.Voltage) {
    let validatedResults = ocs;
    const allValidationResults = [];
    const { validatedEntries: afterMinSICheck, validationResults: minSIResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueSIParseability(entries, 'minimum', characteristic, {
        ignoreEmpty: true,
    }), OcsValidationCheckType.ValuesShouldBeNumericSIParseable, { dropFailingEntries: true, logger });
    validatedResults = afterMinSICheck;
    allValidationResults.push(...minSIResults);
    const { validatedEntries: afterNominalSICheck, validationResults: nominalSIResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueSIParseability(entries, 'nominal', characteristic, {
        ignoreEmpty: true,
    }), OcsValidationCheckType.ValuesShouldBeNumericSIParseable, { dropFailingEntries: true, logger });
    validatedResults = afterNominalSICheck;
    allValidationResults.push(...nominalSIResults);
    const { validatedEntries: afterMaxSICheck, validationResults: maxSIResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueSIParseability(entries, 'maximum', characteristic, {
        ignoreEmpty: true,
    }), OcsValidationCheckType.ValuesShouldBeNumericSIParseable, { dropFailingEntries: true, logger });
    validatedResults = afterMaxSICheck;
    allValidationResults.push(...maxSIResults);
    const signalNames = signals.map((s) => s.signalName);
    const signalNamesSet = new Set(signalNames);
    const { validatedEntries: afterMinRelativeCheck, validationResults: minRelativeResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueBelongsToSet(entries, 'minimumRelativeToSignalName', signalNamesSet, {
        ignoreEmpty: true,
    }), OcsValidationCheckType.RelativeSignalShouldBeValidSignal, { dropFailingEntries: true, logger });
    validatedResults = afterMinRelativeCheck;
    allValidationResults.push(...minRelativeResults);
    const { validatedEntries: afterNominalRelativeCheck, validationResults: nominalRelativeResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueBelongsToSet(entries, 'nominalRelativeToSignalName', signalNamesSet, {
        ignoreEmpty: true,
    }), OcsValidationCheckType.RelativeSignalShouldBeValidSignal, { dropFailingEntries: true, logger });
    validatedResults = afterNominalRelativeCheck;
    allValidationResults.push(...nominalRelativeResults);
    const { validatedEntries: afterMaxRelativeCheck, validationResults: maxRelativeResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueBelongsToSet(entries, 'maximumRelativeToSignalName', signalNamesSet, {
        ignoreEmpty: true,
    }), OcsValidationCheckType.RelativeSignalShouldBeValidSignal, { dropFailingEntries: true, logger });
    validatedResults = afterMaxRelativeCheck;
    allValidationResults.push(...maxRelativeResults);
    const { validatedEntries: afterMinTargetCheck, validationResults: minTargetResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkPropertyValuesDiffer(entries, 'signalName', 'minimumRelativeToSignalName'), OcsValidationCheckType.TargetAndRelativeSignalShouldNotBeEqual, { dropFailingEntries: true, logger });
    validatedResults = afterMinTargetCheck;
    allValidationResults.push(...minTargetResults);
    const { validatedEntries: afterNominalTargetCheck, validationResults: nominalTargetResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkPropertyValuesDiffer(entries, 'signalName', 'nominalRelativeToSignalName'), OcsValidationCheckType.TargetAndRelativeSignalShouldNotBeEqual, { dropFailingEntries: true, logger });
    validatedResults = afterNominalTargetCheck;
    allValidationResults.push(...nominalTargetResults);
    const { validatedEntries: afterMaxTargetCheck, validationResults: maxTargetResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkPropertyValuesDiffer(entries, 'signalName', 'maximumRelativeToSignalName'), OcsValidationCheckType.TargetAndRelativeSignalShouldNotBeEqual, { dropFailingEntries: true, logger });
    validatedResults = afterMaxTargetCheck;
    allValidationResults.push(...maxTargetResults);
    const { validatedEntries: afterMinNomMaxDifferCheck, validationResults: minNomMaxDifferResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => checkMinimumNominalAndMaximumDiffer(entries, characteristic), OcsValidationCheckType.MinimumNominalAndMaximumShouldDiffer, { dropFailingEntries: true, logger });
    validatedResults = afterMinNomMaxDifferCheck;
    allValidationResults.push(...minNomMaxDifferResults);
    const { validatedEntries: afterDuplicateCheck, validationResults: duplicateResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkForDuplicateEntries(entries), OcsValidationCheckType.ConditionsShouldNotBeDuplicated, { dropFailingEntries: true, logger });
    validatedResults = afterDuplicateCheck;
    allValidationResults.push(...duplicateResults);
    return {
        validatedResults,
        validationResults: (0, sigma_engine_shared_1.compactValidationResults)(allValidationResults),
    };
}
function checkMinimumNominalAndMaximumDiffer(entries, characteristic = sigma_engine_shared_1.BoundaryCharacteristic.Voltage) {
    const failures = [];
    const siDefinition = sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[characteristic];
    entries.forEach((entry, i) => {
        try {
            const minValue = entry.minimum ? sigma_engine_shared_1.Utils.parseNumericalSIValue(entry.minimum, siDefinition) : undefined;
            const nominalValue = entry.nominal ? sigma_engine_shared_1.Utils.parseNumericalSIValue(entry.nominal, siDefinition) : undefined;
            const maxValue = entry.maximum ? sigma_engine_shared_1.Utils.parseNumericalSIValue(entry.maximum, siDefinition) : undefined;
            if (minValue !== undefined && maxValue !== undefined && Math.abs(minValue - maxValue) < siDefinition.epsilon) {
                failures.push({
                    index: i,
                    message: `Minimum and maximum values are equal: ${entry.minimum} === ${entry.maximum}`,
                    details: `Signal: ${entry.signalName}, Characteristic: ${entry.characteristic}`,
                });
            }
            if (minValue !== undefined &&
                nominalValue !== undefined &&
                Math.abs(minValue - nominalValue) < siDefinition.epsilon) {
                failures.push({
                    index: i,
                    message: `Minimum and nominal values are equal: ${entry.minimum} === ${entry.nominal}`,
                    details: `Signal: ${entry.signalName}, Characteristic: ${entry.characteristic}`,
                });
            }
            if (nominalValue !== undefined &&
                maxValue !== undefined &&
                Math.abs(nominalValue - maxValue) < siDefinition.epsilon) {
                failures.push({
                    index: i,
                    message: `Nominal and maximum values are equal: ${entry.nominal} === ${entry.maximum}`,
                    details: `Signal: ${entry.signalName}, Characteristic: ${entry.characteristic}`,
                });
            }
        }
        catch (error) {
            return;
        }
    });
    return failures;
}
function mapBoundarySignalNamesToSignalIds(boundaries, signals, type, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    const schema = type === 'amr' ? sigma_engine_shared_1.HilAbsoluteMaximumRatingBoundarySchema : sigma_engine_shared_1.HilOperatingConditionBoundarySchema;
    return boundaries
        .map((b) => {
        const signalId = (0, sigma_engine_shared_1.findSignalIdBySignalName)(b.signalName, signals);
        if (!signalId) {
            logger.warn('MapBoundarySignalNamesToSignalIds_SignalNotFound', {
                signalName: b.signalName,
                boundary: b,
            });
            return null;
        }
        const boundary = {
            signalId: signalId,
            characteristic: b.characteristic,
            minimum: b.minimum,
            minimumRelativeToSignalId: b.minimumRelativeToSignalName ?
                (0, sigma_engine_shared_1.findSignalIdBySignalName)(b.minimumRelativeToSignalName, signals, logger)
                : null,
            nominal: b.nominal ?? null,
            nominalRelativeToSignalId: b.nominalRelativeToSignalName ?
                (0, sigma_engine_shared_1.findSignalIdBySignalName)(b.nominalRelativeToSignalName, signals, logger)
                : null,
            maximum: b.maximum,
            maximumRelativeToSignalId: b.maximumRelativeToSignalName ?
                (0, sigma_engine_shared_1.findSignalIdBySignalName)(b.maximumRelativeToSignalName, signals, logger)
                : null,
        };
        const parseResult = schema.safeParse(boundary);
        if (!parseResult.success) {
            logger.warn('MapBoundarySignalNamesToSignalIds_ValidationFailed', {
                signalName: b.signalName,
                errors: parseResult.error.flatten().fieldErrors,
                boundary: b,
            });
            return null;
        }
        return parseResult.data;
    })
        .filter((boundary) => boundary !== null);
}
function convertHilAmrToDenormalized(hilAmrs, signals) {
    const result = [];
    for (const amr of hilAmrs) {
        const targetSignal = signals.find((s) => s.id === amr.signalId);
        if (!targetSignal) {
            continue;
        }
        const minRelSignal = amr.minimumRelativeToSignalId ? signals.find((s) => s.id === amr.minimumRelativeToSignalId) : null;
        const maxRelSignal = amr.maximumRelativeToSignalId ? signals.find((s) => s.id === amr.maximumRelativeToSignalId) : null;
        result.push({
            signalName: targetSignal.name,
            characteristic: amr.characteristic,
            minimum: amr.minimum ?? null,
            minimumRelativeToSignalName: minRelSignal?.name ?? null,
            maximum: amr.maximum ?? null,
            maximumRelativeToSignalName: maxRelSignal?.name ?? null,
        });
    }
    return result;
}
function convertHilOcToDenormalized(hilOcs, signals) {
    const result = [];
    for (const oc of hilOcs) {
        const targetSignal = signals.find((s) => s.id === oc.signalId);
        if (!targetSignal) {
            continue;
        }
        const minRelSignal = oc.minimumRelativeToSignalId ? signals.find((s) => s.id === oc.minimumRelativeToSignalId) : null;
        const nomRelSignal = oc.nominalRelativeToSignalId ? signals.find((s) => s.id === oc.nominalRelativeToSignalId) : null;
        const maxRelSignal = oc.maximumRelativeToSignalId ? signals.find((s) => s.id === oc.maximumRelativeToSignalId) : null;
        result.push({
            signalName: targetSignal.name,
            characteristic: oc.characteristic,
            minimum: oc.minimum ?? null,
            minimumRelativeToSignalName: minRelSignal?.name ?? null,
            nominal: oc.nominal ?? null,
            nominalRelativeToSignalName: nomRelSignal?.name ?? null,
            maximum: oc.maximum ?? null,
            maximumRelativeToSignalName: maxRelSignal?.name ?? null,
        });
    }
    return result;
}
function convertBoundariesToAmrDenormalized(boundaries, signals) {
    const hilAmrs = sigma_engine_shared_1.BoundaryMapperUtil.getAmrBoundaries(signals, boundaries);
    return convertHilAmrToDenormalized(hilAmrs, signals);
}
function convertBoundariesToOcDenormalized(boundaries, signals) {
    const hilOcs = sigma_engine_shared_1.BoundaryMapperUtil.getOperatingConditionBoundaries(signals, boundaries);
    return convertHilOcToDenormalized(hilOcs, signals);
}
const signal_id_remap_internal_1 = require("../signal-hygiene/signal-id-remap-internal");
function remapSignalIdsInBoundaries(boundaries, signalIdMap, opts) {
    if (boundaries.length === 0) {
        return [];
    }
    const cloned = JSON.parse(JSON.stringify(boundaries));
    cloned.forEach((b, i) => {
        const path = `boundaries[${i}].boundary`;
        (0, signal_id_remap_internal_1.remapBoundaryDefinition)(b.boundary, signalIdMap, path, opts);
        const conditions = b.conditions;
        if (Array.isArray(conditions)) {
            conditions.forEach((c, ci) => {
                const cond = c;
                if (cond.boundary) {
                    (0, signal_id_remap_internal_1.remapBoundaryDefinition)(cond.boundary, signalIdMap, `boundaries[${i}].conditions[${ci}].boundary`, opts);
                }
            });
        }
    });
    return cloned;
}
//# sourceMappingURL=boundary-conditions-denormalized.js.map